import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $isLinkNode,
  AutoLinkNode,
  LinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode } from "@lexical/rich-text";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import {
  IconBold,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconUnderline,
} from "@tabler/icons-react";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import { useCallback, useEffect, useState } from "react";
import Button from "./Button";
import Input from "./Input";
import Text from "./Text";

const theme = {
  paragraph: "editor-paragraph",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
  },
  link: "editor-link",
  list: {
    ul: "editor-list-ul",
    ol: "editor-list-ol",
    listitem: "editor-listitem",
  },
};

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isUnorderedList, setIsUnorderedList] = useState(false);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat("bold"));
          setIsItalic(selection.hasFormat("italic"));
          setIsUnderline(selection.hasFormat("underline"));

          // Check if current selection is in a list
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();

          if ($isListNode(element)) {
            const listType = element.getListType();
            setIsUnorderedList(listType === "bullet");
            setIsOrderedList(listType === "number");
          } else {
            setIsUnorderedList(false);
            setIsOrderedList(false);
          }

          // Check if current selection is on a link
          const node = selection.anchor.getNode();
          const parent = node.getParent();
          setIsLink($isLinkNode(parent) || $isLinkNode(node));
        }
      });
    });
  }, [editor]);

  const openLinkDialog = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = selection.anchor.getNode();
        const linkParent = node.getParent();

        if ($isLinkNode(linkParent)) {
          // Editing existing link
          setLinkUrl(linkParent.getURL());
          setLinkText(
            selection.getTextContent() || linkParent.getTextContent(),
          );
          setIsEditingExistingLink(true);
        } else {
          // Creating new link
          setLinkUrl("");
          setLinkText(selection.getTextContent());
          setIsEditingExistingLink(false);
        }
      }
    });
    setLinkDialogOpen(true);
  }, [editor]);

  const handleInsertLink = useCallback(() => {
    if (linkUrl && linkText) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (isEditingExistingLink) {
            // Update existing link
            const node = selection.anchor.getNode();
            const linkParent = node.getParent();
            if ($isLinkNode(linkParent)) {
              linkParent.setURL(linkUrl);
              linkParent.setTarget("_blank");
              linkParent.setRel("noopener noreferrer");
              // Update text content if different
              const currentText = linkParent.getTextContent();
              if (linkText !== currentText) {
                selection.insertText(linkText);
              }
            }
          } else {
            // Create new link
            // Insert the text first
            selection.insertText(linkText);

            // Create a new selection that covers the inserted text
            const textNode = selection.focus.getNode();
            const endOffset = selection.focus.offset;
            const startOffset = endOffset - linkText.length;

            // Select the inserted text
            selection.setTextNodeRange(
              textNode,
              startOffset,
              textNode,
              endOffset,
            );

            // Apply the link to the selected text
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
              url: linkUrl,
              target: "_blank",
              rel: "noopener noreferrer",
            });
          }
        }
      });
    }
    setLinkDialogOpen(false);
    setLinkUrl("");
    setLinkText("");
    setIsEditingExistingLink(false);
  }, [editor, linkUrl, linkText, isEditingExistingLink]);

  const handleCancelLink = useCallback(() => {
    setLinkDialogOpen(false);
    setLinkUrl("");
    setLinkText("");
    setIsEditingExistingLink(false);
  }, []);

  const handleUnorderedListClick = useCallback(() => {
    if (isUnorderedList) {
      // If already in unordered list, remove it
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      // Otherwise, create unordered list
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, isUnorderedList]);

  const handleOrderedListClick = useCallback(() => {
    if (isOrderedList) {
      // If already in ordered list, remove it
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      // Otherwise, create ordered list
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, isOrderedList]);

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        backgroundColor: "#F8F9FA",
        borderBottom: "1px solid #D4D4D4",
        borderTopLeftRadius: "6px",
        borderTopRightRadius: "6px",
        padding: "8px",
      }}
    >
      <IconButton
        size="small"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        sx={{
          backgroundColor: isBold ? "#1990FF" : "transparent",
          color: isBold ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isBold ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconBold size={18} />
      </IconButton>

      <IconButton
        size="small"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        sx={{
          backgroundColor: isItalic ? "#1990FF" : "transparent",
          color: isItalic ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isItalic ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconItalic size={18} />
      </IconButton>

      <IconButton
        size="small"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        sx={{
          backgroundColor: isUnderline ? "#1990FF" : "transparent",
          color: isUnderline ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isUnderline ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconUnderline size={18} />
      </IconButton>

      <IconButton
        size="small"
        onClick={handleUnorderedListClick}
        sx={{
          backgroundColor: isUnorderedList ? "#1990FF" : "transparent",
          color: isUnorderedList ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isUnorderedList ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconList size={18} />
      </IconButton>

      <IconButton
        size="small"
        onClick={handleOrderedListClick}
        sx={{
          backgroundColor: isOrderedList ? "#1990FF" : "transparent",
          color: isOrderedList ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isOrderedList ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconListNumbers size={18} />
      </IconButton>

      <IconButton
        size="small"
        onClick={openLinkDialog}
        sx={{
          backgroundColor: isLink ? "#1990FF" : "transparent",
          color: isLink ? "white" : "#666",
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isLink ? "#1990FF" : "#E5E5E5",
          },
        }}
      >
        <IconLink size={18} />
      </IconButton>

      <Dialog
        open={linkDialogOpen}
        onClose={handleCancelLink}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            {isEditingExistingLink ? "Edit Link" : "Insert Link"}
          </Text>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Input
            placeholder="Link text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <Input
            placeholder="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button variant="secondary" onClick={handleCancelLink}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertLink}
            disabled={!linkUrl || !linkText}
          >
            {isEditingExistingLink ? "Update" : "Insert"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const OnChangePluginWrapper = ({ onChange }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        onChange(htmlString);
      });
    });
  }, [editor, onChange]);

  return null;
};

const LinkClickPlugin = ({ onLinkClick }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement !== null) {
        prevRootElement.removeEventListener("click", onLinkClick);
      }
      if (rootElement !== null) {
        rootElement.addEventListener("click", onLinkClick);
      }
    });
  }, [editor, onLinkClick]);

  return null;
};

const InitialStatePlugin = ({ initialHtml }) => {
  const [editor] = useLexicalComposerContext();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialHtml && !initialized) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(initialHtml, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
      setInitialized(true);
    }
  }, [editor, initialHtml, initialized]);

  return null;
};

const RichTextEditor = ({ label, value, onChange, placeholder }) => {
  const initialConfig = {
    namespace: "RichTextEditor",
    theme,
    onError: (error) => {
      // Error handling for Lexical editor
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    nodes: [HeadingNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
  };

  const handleLinkClick = useCallback((event) => {
    const target = event.target;
    if (target.tagName === "A" && target.classList.contains("editor-link")) {
      event.preventDefault();
      // The link editing will be handled by the toolbar button when user selects the link
    }
  }, []);

  return (
    <Box>
      {label && (
        <Text component="p" variant="paragraph3" sx={{ mb: 1 }}>
          {label}
        </Text>
      )}
      <Box
        sx={{
          border: "1px solid #D4D4D4",
          borderRadius: "6px",
          backgroundColor: "white",
          "& .editor-input": {
            minHeight: "120px",
            padding: "12px",
            fontSize: "14px",
            color: "#000",
            outline: "none",
            fontFamily: "inherit",
          },
          "& .editor-placeholder": {
            color: "#999",
            overflow: "hidden",
            position: "absolute",
            top: "12px",
            left: "12px",
            userSelect: "none",
            pointerEvents: "none",
          },
          "& .editor-link": {
            color: "#1990FF",
            textDecoration: "underline",
            cursor: "pointer",
          },
          "& .editor-text-bold": {
            fontWeight: "bold",
          },
          "& .editor-text-italic": {
            fontStyle: "italic",
          },
          "& .editor-text-underline": {
            textDecoration: "underline",
          },
          "& .editor-list-ul, & .editor-list-ol": {
            paddingLeft: "24px",
            margin: "0",
          },
          "& .editor-listitem": {
            margin: "4px 0",
          },
        }}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <ToolbarPlugin />
          <Box sx={{ position: "relative" }}>
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={
                <div className="editor-placeholder">{placeholder}</div>
              }
            />
          </Box>
          <OnChangePluginWrapper onChange={onChange} />
          <HistoryPlugin />
          <LinkPlugin />
          <ListPlugin />
          <LinkClickPlugin onLinkClick={handleLinkClick} />
          <InitialStatePlugin initialHtml={value} />
        </LexicalComposer>
      </Box>
    </Box>
  );
};

export default RichTextEditor;
